<template>
  <div class="credit__card">

    <!-- Credit Cards / ACH accounts list -->
    <div v-if="isCC || isACH">
      <transition name="fade-slide">
        <div
          class="cards-list"
          v-if="!newPaymentMethodMode && preparedCardsList.length > 0"
        >
          <h2 class="cards-list__title">{{ selectPaymentMethodTitle }}</h2>
          <credit-card-radio-block
            v-for="card in preparedCardsList"
            :key="card.id"
            :cards-list="preparedCardsList"
            :card="card"
            :show-delete-card="false"
          />
        </div>
      </transition>

      <button
        class="btn btn--color-outline-dark form__button"
        @click="toggleNewPaymentMode"
        :disabled="isLoading"
      >
        {{ paymentMethodButtonText }}
      </button>
    </div>

    <!-- Credit Card Cardknox Form -->
    <transition name="fade-slide">
      <credit-card-cardknox-template
        v-if="showCardknoxCC"
        ref="cardknoxTemplate"
        :i-fields-options="iFieldsOptions"
      />
    </transition>

    <!-- Credit Card Stripe Form -->
    <transition name="fade-slide">
      <credit-card-stripe-template
        v-if="showStripeCC"
        :has-card-error="hasCardError"
        :stripe-item-i-d="stripeItemID"
      />
    </transition>

    <!-- ACH Cardknox Form -->
    <transition name="fade-slide">
      <a-c-h-cardknox-template
        v-if="showCardknoxACH"
        ref="cardknoxACHTemplate"
        :i-fields-options="iFieldsOptions"
      />
    </transition>

    <!-- Billing Address Form -->

    <transition name="fade-slide">
      <div v-if="showBillingAddressReadonly">
        <billing-address-read-only-template
          @edit="toggleEditingBillingAddressForm"
        />
      </div>
    </transition>
    <transition name="fade-slide">
      <billing-address-template
        v-if="showBillingAddressForm"
        :editing-mode="isEditingBillingAddress"
        :creating-mode="newPaymentMethodMode"
        @cancel="toggleEditingBillingAddressForm"
      />
    </transition>

    <!-- Terms & Conditions -->
    <label
      v-if="authSettings.use_terms_and_conditions"
      class="center nice-checkbox"
      :class="{'is-invalid': $v.terms.$dirty && !$v.terms.sameAs }"
    >
      <input
        v-model="terms"
        @change="$v.terms.$touch()"
        class="terms-and-conditions-link"
        type="checkbox"
      />
      <span></span>
      <a
        href="#"
        class="terms-and-conditions-link"
        @click="isShowModal = true"
      >
        I agree to terms & conditions
      </a>
    </label>

    <!-- Validation Errors -->
    <div
      v-for="(msg, index) in errorMessages"
      :key="index"
      class="form__error card__error"
    >
      {{ msg }}
    </div>
    <div
      v-if="disabledButtonInfoMessage && forceDisablingSendButton"
      class="form__disabled_error_message"
    >
      {{ disabledButtonInfoMessage }}
    </div>

    <!-- Submit Buttons -->
    <div class="credit-card__buttons">
      <button
        class="btn btn--color-outline-inactive credit-card__button"
        @click="$store.commit('applicationRegistration/DECREMENT_TAB_INDEX')"
        :disabled="isLoading"
      >Previous</button>
      <button
        class="btn btn--color-dark credit-card__button"
        @click="payout"
        :disabled="isBtnDisabled || hasCardError || isLoading"
      >
        <span v-html="submitButtonText"></span>
      </button>
    </div>

    <TermsAndConditionalModal
      :is-show-modal="isShowModal"
      @closeModal="isShowModal = false"
    />

  </div>
</template>

<script>

import 'vue-select/dist/vue-select.css'
import { sameAs } from 'vuelidate/lib/validators'

import TermsAndConditionalModal from '@/components/site/TermsAndConditionalModal.vue'
import uniqueId from 'lodash/uniqueId'
import { mapGetters } from 'vuex'
import CreditCardStripeTemplate from '@/components/site/CreditCardStripeTemplate.vue'
import CreditCardCardknoxTemplate from '@/components/site/CreditCardCardknoxTemplate.vue'
import ACHCardknoxTemplate from '@/components/site/ACHCardknoxTemplate.vue'
import BillingAddressTemplate from '@/components/site/BillingAddressTemplate.vue'
import CreditCardRadioBlock from '@/components/site/CreditCardRadioBlock.vue'
import cardknoxMixin from '@core/mixins/cardknoxMixin.js'
import stripeMixin from '@core/mixins/stripeMixin'
import BillingAddressReadOnlyTemplate from '@/components/site/BillingAddressReadOnlyTemplate.vue'
import store from '@/store'

  export default {
    mixins: [stripeMixin, cardknoxMixin],
    components: {
      BillingAddressReadOnlyTemplate,
      CreditCardRadioBlock,
      BillingAddressTemplate,
      ACHCardknoxTemplate,
      CreditCardCardknoxTemplate,
      CreditCardStripeTemplate,
      TermsAndConditionalModal,
    },
    props: {
      btnText: {
        type: String,
        default: '',
      },
      isBtnDisabled: {
        type: Boolean,
        default: false,
      },
      hideCC: {
        type: Boolean,
        default: false,
      },
      hideACH: {
        type: Boolean,
        default: false,
      },
      forceDisablingSendButton: {
        type: Boolean,
        default: false,
      },
      disabledButtonInfoMessage: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        stripe: null,
        stripeItemID: '',

        errorTCMessage: '',
        errorEmptyCard: '',
        errorValidation: '',

        terms: false,
        isShowModal: false,
        newPaymentMethodMode: false,

        isEditingBillingAddress: false,
      }
    },
    validations() {
      if (this.authSettings.use_terms_and_conditions) {
        return {
          terms: {
            sameAs: sameAs(true)
          }
        }
      }

      return {}
    },
    beforeMount() {
      this.stripeItemID = uniqueId()
    },
    async created() {
      await this.clearForms()
    },
    computed: {
      ...mapGetters({
        publishableKey: 'paymentSettings/getStripePublishKey',
        isCardknox: 'paymentSettings/getIsCardknox',
        isStripe: 'paymentSettings/getIsStripe',
        iFieldsOptions: 'paymentSettings/getCardknoxIFieldsOptions',
        authSettings: 'authSettings/getAuthSettings',
        cardsList: 'cards/getCardsList',
        selectedCardInfo: 'cards/getSelectedCardInfo',
        selectedCard: 'cards/getSelectedCreditCard',
        billingAddress: 'cards/getBillingAddressData',
        billingAddressFormData: 'formValidation/getBillingAddressFormData',
        billingAddressRequiredFields: 'formValidation/getBillingAddressRequiredFields',
      }),
      isLoading: {
        get() {
          return store.getters['applicationRegistration/getIsLoading']
        },
        set(value) {
          this.$store.commit('applicationRegistration/SET_IS_LOADING', value)
        },
      },
      errorMessages() {
        return [this.errorTCMessage, this.errorEmptyCard, this.errorValidation].filter(Boolean)
      },
      hasBillingAddress() {
        return (this.selectedCardInfo?.has_billing_address ?? false) && (this.billingAddress?.id ?? false)
      },
      showBillingAddressReadonly() {
        return this.hasBillingAddress && !this.isEditingBillingAddress && !this.newPaymentMethodMode
      },
      showBillingAddressForm() {
        if (this.isEditingBillingAddress) return true
        if (this.isNewPaymentBillingRequired) return true
        return this.selectedCardInfo && !this.hasBillingAddress
      },
      isNewPaymentBillingRequired() {
        return this.newPaymentMethodMode && (this.isCC || this.isACH)
      },
      preparedCardsList() {
        if (this.isCC) {
          return this.cardsList.filter(card => card.type.toLowerCase() !== 'ach')
        }
        if (this.isACH) {
          return this.cardsList.filter(card => card.type.toLowerCase() === 'ach')
        }
        return []
      },
      showCardknoxCC() {
        return this.newPaymentMethodMode && this.isCC && this.isCardknox
      },
      showStripeCC() {
        return this.newPaymentMethodMode && this.isCC && this.isStripe
      },
      showCardknoxACH() {
        return this.newPaymentMethodMode && this.isACH && this.isCardknox
      },
      paymentMethodButtonText() {
        return this.newPaymentMethodMode ? this.returnPaymentMethodButton : this.addNewPaymentMethodButton
      },
      addNewPaymentMethodButton() {
        return this.isACH ? 'Add Bank Account +' : 'Add New Credit Card +'
      },
      returnPaymentMethodButton() {
        return this.isACH ? 'Back to Saved Accounts' : 'Back to Saved Cards'
      },
      selectPaymentMethodTitle() {
        return this.isACH ? 'Select Bank Account' : 'Select Card'
      },
      hasCardError() {
        return !!this.$store.getters['formValidation/getCreditCardError']('card_number')
          || !!this.$store.getters['formValidation/getCreditCardError']('card_expiry')
          || !!this.$store.getters['formValidation/getCreditCardError']('card_cvc')
      },
      submitButtonText() {
        if (this.btnText) return this.btnText
        return 'Submit form, <br>continue to documents'
      },
      isCC() {
        return !this.hideCC && this.hideACH
      },
      isACH() {
        return this.hideCC && !this.hideACH
      },
      cardholderName() {
        return this.$store.getters['formValidation/getCreditCardField']('cardholder_name')
      },
      cardExpiry() {
        return this.$store.getters['formValidation/getCreditCardField']('card_expiry')
      },
      achName() {
        return this.$store.getters['formValidation/getACHField']('ach_name')
      },
      achRouting() {
        return this.$store.getters['formValidation/getACHField']('ach_routing')
      },
    },
    watch: {
      isCC(val, prevVal) {
        if (val !== prevVal) {
          this.loadCards()
          this.clearValidationErrors()
        }
      },
      newPaymentMethodMode(val, prevVal) {
        if (val && val !== prevVal && this.isStripe) {
          this.initStripe(this.publishableKey, this.stripeItemID)
        }
      },
      isACH(val, prevVal) {
        if (val && val !== prevVal) {
          this.loadCards()
          this.clearValidationErrors()
        }
      }
    },
    beforeDestroy () {
      this.clearForms()

      if (this.isStripe) {
        this.destroyMountedForm()
      }
    },
    methods: {
      toggleEditingBillingAddressForm() {
        this.isEditingBillingAddress = !this.isEditingBillingAddress
      },
      toggleNewPaymentMode() {
        this.newPaymentMethodMode = !this.newPaymentMethodMode
        this.isEditingBillingAddress = false
        this.clearForms()
      },
      async loadCards() {
        await this.$store.dispatch('cards/loadCards')
      },
      async clearForms() {
        this.$store.commit('formValidation/resetBillingAddressForm')
        this.$store.commit('formValidation/resetCreditCardFormData')
        this.$store.commit('formValidation/resetACHFormData')
      },
      async clearValidationErrors() {
        this.errorTCMessage = ''
        this.errorEmptyCard = ''
        this.errorValidation = ''
        this.$store.commit('formValidation/resetAllErrors')
      },
      async validateForm() {
        await this.clearValidationErrors()
        let isValid = true

        // helper: commit validation error
        const setError = (module, field, value) => {
          this.$store.commit(`formValidation/${module}`, { field, value })
          if (value) isValid = false
        }

        // helper: required check
        const requireField = (value) => Boolean(value && value.trim())

        // ------------ BILLING ADDRESS -------------
        if (this.showBillingAddressForm) {
          const form = this.billingAddressFormData
          const required = this.billingAddressRequiredFields

          required.forEach(field => {
            const ok = requireField(form[field])
            setError('setBillingAddressFormError', field, !ok)
          })
        }

        // ------------ CARDHOLDER NAME -------------
        if (this.newPaymentMethodMode && this.isCC) {
          const ok = requireField(this.cardholderName)
          setError('setCreditCardFormError', 'cardholder_name', !ok)
        }

        // ------------ STRIPE -------------
        if (this.newPaymentMethodMode && this.isStripe) {
          const stripe = [
            { el: this.cardNumberStripeElement, field: 'card_number', msg: 'Card number is required' },
            { el: this.cardExpiryStripeElement, field: 'card_expiry', msg: 'Expiration date is required' },
            { el: this.cardCvcStripeElement, field: 'card_cvc', msg: 'CVC is required' }
          ]

          stripe.forEach(item => {
            const ok = item.el?._complete === true
            setError('setCreditCardFormError', item.field, ok ? false : item.msg)
          })
        }

        // ------------ CARDKNOX CC -------------
        if (this.newPaymentMethodMode && this.isCardknox && this.isCC) {
          const expiryError = await this.$store.dispatch('formValidation/validateCardknoxExpiry', this.cardExpiry)
          setError('setCreditCardFormError', 'card_expiry', expiryError)
        }

        // ------------ CARDKNOX ACH -------------
        if (this.newPaymentMethodMode && this.isCardknox && this.isACH) {
          setError('setACHFormError', 'ach_name', !requireField(this.achName))
          setError('setACHFormError', 'ach_routing', !requireField(this.achRouting))
        }

        // --------- SELECTED EXISTING CARD ---------
        if (this.newPaymentMethodMode) {
          this.$store.commit('cards/SET_SELECTED_CREDIT_CARD', null)
        } else {
          if (!this.selectedCard && (this.isCC || this.isACH)) {
            this.errorEmptyCard = 'Please choose a payment method to continue.'
            isValid = false
          }
        }

        // ------------ vuelidate (check regular fields) -------------
        this.$v.$touch()
        if (this.$v.$invalid) {
          isValid = false
          this.errorValidation = 'Validation failed. Please check the form fields.'
        }

        // ------------ TERMS -------------
        if (this.authSettings.use_terms_and_conditions && this.$v.terms.$invalid) {
          this.errorTCMessage = 'Please, agree to our terms and conditions'
          isValid = false
        }

        return isValid
      },
      async payout() {
        // Step 1: Validate form
        const ok = await this.validateForm()
        if (!ok) return

        // Step 2: If user selected a saved card
        if ((this.isCC || this.isACH) && this.selectedCard) {
          const preparedData = this.showBillingAddressForm
            ? this.billingAddressFormData
            : null
          this.$emit('authCreditCard', preparedData)
          return
        }

        // Step 3: Stop if card info invalid
        if (this.hasCardError) {
          this.errorEmptyCard = 'Invalid payment information.'
          return
        }

        // Step 4: Process payment depending on the processor
        if (this.isCardknox) {
          if (this.isACH) return await this.handleCardknoxACHPayment()
          if (this.isCC)  return await this.handleCardknoxCCPayment()
        }

        if (this.isStripe && this.isCC) {
          return await this.handleStripePayment()
        }

        // Step 5: If payment method type is neither CC nor ACH
        if (!this.isCC && !this.isACH) {
          this.$emit('authCreditCard')
        }
      },
      async handleStripePayment() {
        const token = await this.fetchStripeCardToken(this.stripeItemID)

        token.gateway = 'stripe'
        token.billingAddress = this.billingAddressFormData
        token.card.cardholder_name = this.cardholderName

        this.$emit('authCreditCard', token)
      },
      async handleCardknoxCCPayment() {
        if (!this.$refs.cardknoxTemplate.$refs.ifieldCardRef) {
          return
        }

        await this.fetchCardknoxCardToken()

        if (!this.cardToken) return

        try {
          const cardNumber = this.cardToken.split(';')[0]
          const match = this.cardToken.match(/cc_(.*?)_keyed/)
          const cardType = match ? match[1] : 'unknown'

          const [month, year] = this.cardExpiry.split('/')

          this.$emit('authCreditCard', {
            id: this.cardToken,
            card: {
              exp_month: month,
              exp_year: year,
              last4: cardNumber.slice(-4),
              brand: cardType,
              cardholder_name: this.cardholderName,
            },
            billingAddress: this.billingAddressFormData,
            gateway: 'cardknox',
          })
        } catch (error) {
          // document.getElementById('#' + this.cardknoxItemID + 'card-error').innerHTML = error.message
          console.error('Ошибка токенизации:', error)
        }
      },
      async fetchCardknoxACHToken() {
        return new Promise((resolve) => {
          this.$refs.cardknoxACHTemplate.$refs.ifieldACHRef._onToken = (response) => {
            this.cardToken = response?.data?.xToken || ""
            resolve(this.cardToken)
          }
          this.$refs.cardknoxACHTemplate.$refs.ifieldACHRef.getToken()
        })
      },
      async handleCardknoxACHPayment() {
        if (!this.$refs.cardknoxACHTemplate.$refs.ifieldACHRef) {
          return
        }

        await this.fetchCardknoxACHToken()

        if (!this.cardToken) return

        const achNumber = (typeof this.cardToken === 'string' && this.cardToken.includes(';'))
          ? this.cardToken.split(';')[0]
          : ''

        try {
          this.$emit('authCreditCard', {
            id: this.cardToken,
            type: 'ACH',
            gateway: 'cardknox',
            ach_routing: this.achRouting,
            ach_last4: achNumber.slice(-4),
            ach_name: this.achName,
            billingAddress: this.billingAddressFormData,
          })
        } catch (error) {
          // document.getElementById('#' + this.cardknoxItemID + 'card-error').innerHTML = error.message
          console.error('Ошибка токенизации:', error)
        }
      },
    }
  }
</script>

<style lang="scss">
.payment__plan{
    margin-bottom: 30px;
    &-monthly{
        font-size: 20px;
        line-height: 26px;
        text-align: right;
        letter-spacing: -0.01em;
    }
}
.row {
    display: flex;
    flex-wrap: wrap;
    margin-right: -15px;
    margin-left: -15px;
}
.is-invalid{
    color: #f00;
}
.col-6 {
    position: relative;
    width: 100%;
    min-height: 1px;
    padding-right: 15px;
    padding-left: 15px;
    flex: 0 0 50%;
    max-width: 50%;
}
.terms-and-conditions-link {
  cursor: pointer;
  padding-left: 27px;
  color: #000;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
